import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import SubPopup from "../../../components/NewJob/SubPopup/popup";
import { errorHandler } from "../../../utils";
import {
  companyProfile,
  findClientSubscription,
  postSubscription,
} from "../../../services/clientCalls";
import styles from "./review.module.css";
import {
  IconCheck,
  IconChevronsLeft,
  IconCircleCheck,
  IconUserCircle,
  IconX,
} from "@tabler/icons-react";

function pad(n) {
  return n < 10 ? "0" + n : String(n);
}

const Review = () => {
  let navigate = useNavigate();
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [subscribeIsLoading, setSubscribeIsLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [selectedPlanName, setSelectedPlanName] = useState(null);
  const [infoData, setInfoData] = useState(false);
  const [secs, setSecs] = useState(24 * 3600);
  const [pct, setPct] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => {
    const planName = localStorage.getItem("subscriptionPlanName");
    const savedPlan = localStorage.getItem("subscriptionPlan");

    if (!planName || !savedPlan) return;

    try {
      setSelectedPlan(JSON.parse(savedPlan));
      setSelectedPlanName(planName);
      setShowSubscriptionModal(true);
    } catch {
      localStorage.removeItem("subscriptionPlan");
      localStorage.removeItem("subscriptionPlanName");
    }
  }, []);

  const handleFindClientSubscription = async () => {
    try {
      const payload = {
        name: selectedPlanName,
      };
      if (selectedPlanName) {
        const res = await findClientSubscription(payload);
        const subscriptionId = res?.data?.data?.subscription?._id;
        if (subscriptionId) {
          return subscriptionId;
        }
      } else {
        errorHandler("Please select a plan", "Error");
        return null;
      }
    } catch (error) {
      error?.response?.data
        ? errorHandler(error?.response?.data?.errors, "Error")
        : errorHandler(
            "Network error: Something went wrong, Please check your internet connection and try again.",
            "Error",
          );
    }
  };

  const handleSubscription = useCallback(async () => {
    setSubscribeIsLoading(true);
    try {
      const subscriptionId = await handleFindClientSubscription();
      if (subscriptionId) {
        const res = await postSubscription(subscriptionId);
        if (res?.data?.data?.data?.data?.authorization_url) {
          localStorage.removeItem("subscriptionPlan");
          localStorage.removeItem("subscriptionPlanName");
          window.location.href = res?.data?.data?.data?.data?.authorization_url;
        }
      }
    } catch (error) {
      setSubscribeIsLoading(false);
      error?.response?.data
        ? errorHandler(error?.response?.data?.errors, "Error")
        : errorHandler(
            "Network error: Something went wrong, Please check your internet connection and try again.",
            "Error",
          );
    } finally {
      setSubscribeIsLoading(false);
    }
  }, [selectedPlanName]);

  const handleCloseSubscriptionModal = useCallback(() => {
    setShowSubscriptionModal(false);
    localStorage.removeItem("subscriptionPlanName");
    localStorage.removeItem("subscriptionPlan");
  }, []);

  const getCompanyProfile = async () => {
    try {
      const res = await companyProfile();
      if (res?.data?.data?.client) {
        setInfoData(res.data.data.client);
      }
    } catch (error) {}
  };

  useEffect(() => {
    getCompanyProfile();
  }, []);

  const hours = Math.floor(secs / 3600);
  const mins = Math.floor((secs % 3600) / 60);
  const secs_ = secs % 60;

  const socialLinks = {
    facebookUrl: infoData?.facebookUrl,
    twitterUrl: infoData?.twitterUrl,
    instagramUrl: infoData?.instagramUrl,
    linkedinUrl: infoData?.linkedinUrl,
    youtubeUrl: infoData?.youtubeUrl,
  };
  const hasValue = (value) =>
    typeof value === "string" ? value.trim().length > 0 : Boolean(value);

  const checkSocial = Object.values(socialLinks).some(hasValue);
  const checkAbout = !!infoData?.summary?.trim();
  const checkGeneralInfo =
    hasValue(infoData.companyName) && hasValue(infoData.companyAddress);
  const checkClassification =
    (Array.isArray(infoData.industry) && infoData.industry.length > 0) ||
    hasValue(infoData.companySize);
  const checkLogo = hasValue(infoData?.clientLogo);

  const CHECKLIST = [
    { label: "Company logo", done: checkLogo },
    { label: "Company type, industry & size", done: checkClassification },
    {
      label: "General information (CAC, TIN, EIN, address)",
      done: checkGeneralInfo,
    },
    { label: "Social links", done: checkSocial },
    { label: "Company summary", done: checkAbout },
  ];

  // Compute completeness
  const done = CHECKLIST?.filter((c) => c.done)?.length;
  const targetPct = Math.round((done / CHECKLIST.length) * 100) || 0;

  // Animate progress bar after mount
  useEffect(() => {
    const t = setTimeout(() => setPct(targetPct), 100);
    return () => clearTimeout(t);
  }, [targetPct]);

  // Start countdown
  useEffect(() => {
    timerRef.current = setInterval(() => {
      setSecs((s) => (s > 0 ? s - 1 : 0));
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, []);

  return (
    <>
      <div className={styles.obComplete} id="completePanel">
        <div className={styles.completeCard}>
          {/* Top section */}
          <div className={styles.completeTop}>
            <div className={styles.iconRing}>
              <IconCircleCheck />
            </div>
            <div className={styles.completeTitle}>
              Profile Submitted for Review!
            </div>
            <div className={styles.completeSub}>
              The hard part's over. Our team will verify your information — this
              usually takes no more than 24 - 48 hours.
            </div>

            {/* Countdown */}
            <div className={styles.countdownBox}>
              <div className={styles.countdownUnit}>
                <div className={styles.countdownVal}>{pad(hours)}</div>
                <div className={styles.countdownLbl}>Hrs</div>
              </div>
              <div className={styles.countdownSep}>:</div>
              <div className={styles.countdownUnit}>
                <div className={styles.countdownVal}>{pad(mins)}</div>
                <div className={styles.countdownLbl}>Mins</div>
              </div>
              <div className={styles.countdownSep}>:</div>
              <div className={styles.countdownUnit}>
                <div className={styles.countdownVal}>{pad(secs_)}</div>
                <div className={styles.countdownLbl}>Secs</div>
              </div>
            </div>

            {/* Progress */}
            <div className={styles.progressWrap}>
              <div className={styles.progressLabel}>
                <span>Profile Completeness</span>
                <span>{pct}%</span>
              </div>
              <div className={styles.progressBar}>
                <div
                  className={styles.progressFill}
                  style={{ width: `${pct}%` }}
                />
              </div>
            </div>
          </div>

          {/* Bottom checklist */}
          <div className={styles.completeBottom}>
            <div className={styles.checklistTitle}>What you submitted</div>
            <div className={styles.checklist}>
              {CHECKLIST.map(({ label, done }) => (
                <div key={label} className={styles.checklistItem}>
                  <div
                    className={`${styles.checkIcon} ${done ? styles.checkIconDone : styles.checkIconTodo}`}
                  >
                    {done ? <IconCheck size={11} /> : <IconX size={11} />}
                  </div>
                  {label}
                </div>
              ))}
            </div>
          </div>

          {/* Actions */}
          <div className={styles.completeActions}>
            <Link to="/company-profile" className={styles.btnGlass}>
              <IconUserCircle /> View My Profile
            </Link>
            <button
              className={styles.btnBackGlass}
              onClick={() => navigate(-1)}
            >
              <IconChevronsLeft /> Back
            </button>
          </div>
        </div>
      </div>

      {/* Order Summary Modal */}
      {showSubscriptionModal && (
        <SubPopup
          showModal={handleCloseSubscriptionModal}
          onClick={handleSubscription}
          subscribeIsLoading={subscribeIsLoading}
          selectedPlan={selectedPlan}
        />
      )}
    </>
  );
};

export default Review;
